/* ========================================================================== *\
___________       __         .__
\_   _____/ _____/  |________|__| ____
 |    __)_ /    \   __\_  __ \  |/  _ \
 |        \   |  \  |  |  | \/  (  <_> )
/_______  /___|  /__|  |__|  |__|\____/
        \/     \/
\* ========================================================================== */
// หน้าต่างแก้ข้อความแจ้งเตือน Telegram ต่อเหตุการณ์ (รอบ 2 ข้อ A5 · 25 ต.ค. 2569)
// แม่แบบเขียนเป็นข้อความธรรมดา ใส่ {ตัวแปร} จากชิป และ *ตัวหนา* ได้ · พรีวิวจากเซิร์ฟเวอร์ด้วยข้อมูลตัวอย่าง
// บันทึก → OPTIONS /notify_setting/save_template · ว่าง/เท่าค่าตั้งต้น = กลับไปใช้ค่าตั้งต้น
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import { FetchData } from 'services/Request';

const ERR = {
  template_unknown_var: 'มีตัวแปรที่ระบบไม่รู้จัก ใช้ได้เฉพาะตัวแปรในชิปด้านบน',
  template_too_long: 'ข้อความยาวเกิน 2,000 ตัวอักษร',
  template_not_found: 'ไม่พบแม่แบบนี้',
  template_table_missing:
    'เซิร์ฟเวอร์ยังไม่รองรับแก้ข้อความ (ยังไม่รันสคริปต์ฐานข้อมูล 0029) — แจ้งไอที',
  permission_denied: 'บัญชีนี้ไม่มีสิทธิ์ตั้งค่าแจ้งเตือน',
};

export default function NotifyTemplateDialog({
  open,
  onClose,
  eventCode,
  eventName,
  templates,
  canEdit,
  token,
  mod,
  onSaved,
  onNotify,
}) {
  const mine = templates.filter((tp) => tp.event === eventCode);
  const [code, setCode] = useState('');
  const [text, setText] = useState('');
  const [preview, setPreview] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const areaRef = useRef(null);
  const caret = useRef(null); // ตำแหน่งเคอร์เซอร์หลังแทรกตัวแปร — ตั้งหลัง React เขียนค่าใหม่ (rAF เคยแพ้การพิมพ์ตัวถัดไป · รีเช็ค 27 ต.ค. 2569)
  useLayoutEffect(() => {
    const el = areaRef.current;
    if (caret.current === null || !el) return;
    const pos = caret.current;
    caret.current = null;
    el.focus();
    el.setSelectionRange(pos, pos);
  });
  const timer = useRef(null);
  const seq = useRef(0); // ลำดับคำขอพรีวิว — ใช้แค่คำตอบล่าสุด
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      clearTimeout(timer.current);
    };
  }, []);

  /* เปิดหน้าต่าง = เริ่มที่แม่แบบแรกของเหตุการณ์นั้น */
  useEffect(() => {
    if (!open || !mine.length) return;
    const first = mine[0];
    setCode(first.code);
    setText(first.template);
    setPreview(first.preview);
    setError('');
  }, [open, eventCode]);

  const current = mine.find((tp) => tp.code === code) || null;
  const isDefault = current ? text.trim() === current.default.trim() || !text.trim() : true;

  const pickCode = (c) => {
    const tp = mine.find((x) => x.code === c);
    if (!tp) return;
    /* ทิ้งพรีวิวของแบบเดิมที่ยังค้าง (รีเช็ค 26 ต.ค.: ตอบช้าแล้ว error ของแบบเดิมมาปิดปุ่มบันทึกของแบบใหม่) */
    clearTimeout(timer.current);
    seq.current += 1;
    setCode(c);
    setText(tp.template);
    setPreview(tp.preview);
    setError('');
  };

  /* พรีวิวจากเซิร์ฟเวอร์ (ตัวเดียวกับที่ใช้ส่งจริง) หน่วง 400 มิลลิวินาทีหลังพิมพ์ */
  const askPreview = (value) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      seq.current += 1;
      const mineSeq = seq.current;
      FetchData('OPTIONS', `/${mod}/preview_template`, token, {
        code,
        template: value,
        _t: Date.now(),
      })
        .then((r) => {
          if (!alive.current || mineSeq !== seq.current || r.status !== 'ok') return;
          setPreview(r.preview || '');
          setError(r.error ? ERR[r.error] || r.error : '');
        })
        .catch(() => {});
    }, 400);
  };

  const change = (value) => {
    setText(value);
    askPreview(value);
  };

  /* กดชิป = แทรกตัวแปรตรงตำแหน่งเคอร์เซอร์ */
  const insertVar = (name) => {
    const el = areaRef.current;
    const tag = `{${name}}`;
    if (!el || typeof el.selectionStart !== 'number') {
      change(`${text}${tag}`);
      return;
    }
    const s = el.selectionStart;
    const e = el.selectionEnd;
    const next = text.slice(0, s) + tag + text.slice(e);
    caret.current = s + tag.length;
    change(next);
  };

  const save = (reset) => {
    if (!current || busy) return;
    setBusy(true);
    FetchData('OPTIONS', `/${mod}/save_template`, token, {
      code,
      template: reset ? '' : text,
      _t: Date.now(),
    })
      .then((r) => {
        if (!alive.current) return;
        setBusy(false);
        if (r.status === 'ok') {
          onNotify(reset ? 'กลับไปใช้ข้อความตั้งต้นแล้ว' : 'บันทึกข้อความแล้ว', 'success');
          onSaved();
          onClose();
        } else {
          onNotify(ERR[r.msg] || 'บันทึกไม่สำเร็จ ลองใหม่อีกครั้ง', 'error');
        }
      })
      .catch(() => {
        if (alive.current) {
          setBusy(false);
          onNotify('บันทึกไม่สำเร็จ ลองใหม่อีกครั้ง', 'error');
        }
      });
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth='md'>
      <DialogTitle>ข้อความแจ้งเตือน · {eventName}</DialogTitle>
      <DialogContent dividers>
        {mine.length > 1 && (
          <MDInput
            label='แบบข้อความ'
            select
            fullWidth
            size='small'
            value={code}
            onChange={(e) => pickCode(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          >
            {mine.map((tp) => (
              <MenuItem key={tp.code} value={tp.code}>
                {tp.name}
                {tp.custom === 'Y' ? ' · แก้แล้ว' : ''}
              </MenuItem>
            ))}
          </MDInput>
        )}
        <MDBox display='flex' gap={2} flexWrap='wrap'>
          <MDBox flex='1 1 360px' minWidth={0}>
            <MDTypography variant='caption' fontWeight='bold' display='block' mb={0.5}>
              ตัวแปร · กดเพื่อแทรก
            </MDTypography>
            <MDBox display='flex' flexWrap='wrap' gap={0.5} mb={1.5}>
              {current &&
                Object.entries(current.vars).map(([name, label]) => (
                  <Chip
                    key={name}
                    size='small'
                    label={`{${name}}`}
                    title={label}
                    disabled={!canEdit}
                    onClick={() => insertVar(name)}
                  />
                ))}
            </MDBox>
            <MDInput
              inputRef={areaRef}
              label='ข้อความ · ใช้ *ตัวหนา* ได้'
              fullWidth
              multiline
              minRows={8}
              value={text}
              onChange={(e) => change(e.target.value)}
              error={Boolean(error)}
              helperText={error || `${text.length}/2000 ตัวอักษร`}
              InputProps={{ disabled: !canEdit }}
              InputLabelProps={{ shrink: true }}
            />
          </MDBox>
          <MDBox flex='1 1 300px' minWidth={0}>
            <MDTypography variant='caption' fontWeight='bold' display='block' mb={0.5}>
              ตัวอย่างข้อความที่จะส่ง (ข้อมูลสมมุติ)
            </MDTypography>
            <MDBox
              sx={{
                bgcolor: '#E7F3FF',
                borderRadius: 2,
                p: 1.5,
                minHeight: 180,
                whiteSpace: 'pre-wrap',
                fontSize: 14,
                lineHeight: 1.5,
                border: '1px solid #C9DFF5',
              }}
            >
              {preview}
            </MDBox>
          </MDBox>
        </MDBox>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <MDBox>
          {canEdit && current && (current.custom === 'Y' || !isDefault) && (
            <MDButton variant='text' color='error' disabled={busy} onClick={() => save(true)}>
              คืนค่าเริ่มต้น
            </MDButton>
          )}
        </MDBox>
        <MDBox display='flex' gap={1}>
          <MDButton variant='outlined' color='secondary' disabled={busy} onClick={onClose}>
            {canEdit ? 'ยกเลิก' : 'ปิด'}
          </MDButton>
          {canEdit && (
            <MDButton
              variant='gradient'
              color='dark'
              disabled={busy || Boolean(error) || !text.trim()}
              onClick={() => save(false)}
            >
              {busy ? 'กำลังบันทึก…' : 'บันทึก'}
            </MDButton>
          )}
        </MDBox>
      </DialogActions>
    </Dialog>
  );
}

NotifyTemplateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  eventCode: PropTypes.string.isRequired,
  eventName: PropTypes.string.isRequired,
  templates: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      event: PropTypes.string,
      name: PropTypes.string,
      vars: PropTypes.objectOf(PropTypes.string),
      default: PropTypes.string,
      template: PropTypes.string,
      custom: PropTypes.string,
      preview: PropTypes.string,
    })
  ).isRequired,
  canEdit: PropTypes.bool.isRequired,
  token: PropTypes.string.isRequired,
  mod: PropTypes.string.isRequired,
  onSaved: PropTypes.func.isRequired,
  onNotify: PropTypes.func.isRequired,
};
